import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { Link } from '@/components/link';
import { ProductTileCartOrQuickAdd } from '@/components/product-tile-custom/product-tile-cart-or-quick-add';
import { productCardFocusRingClass } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { getDisplayQuantity } from '@/lib/sold-by-weight';
import { ProductTileImageCustom } from '@/components/product-tile-custom/product-tile-image-custom';
import { ProductTileTagOverflowRow } from '@/components/product-tile-custom/product-tile-tag-overflow-row';
import { WishlistIconButton } from '@/components/buttons/wishlist-icon-button';
import type { ShopperSearch, ShopperBasketsV2 } from '@/scapi';
import type { TFunction } from 'i18next';

const SAND_PLACEHOLDER = 'bg-sand-95';

type SearchHit = ShopperSearch.schemas['ProductSearchHit'];
type ProductItem = ShopperBasketsV2.schemas['ProductItem'];

export interface ProductTileCustomVerticalCardProps extends ComponentProps<'div'> {
    product: SearchHit;
    productName: string;
    className?: string;
    brand?: string;
    productUrl: string;
    effectiveImgAspectRatio: number;
    handleProductClick?: (product: SearchHit) => void;
    productHighlight?: string;
    eatingList: string[];
    priceAndWeight: ReactNode;
    tileBasketLine: ProductItem | undefined;
    basketControlsDisabled?: boolean;
    t: TFunction<'product'>;
}

/**
 * Figma card-product (37:37205): image + txt (title, .bar info, add button) with spacing/200 between sections.
 */
function ProductTileCustomVerticalCardInner(
    {
        product,
        productName,
        brand,
        productUrl,
        effectiveImgAspectRatio,
        handleProductClick,
        productHighlight,
        eatingList,
        priceAndWeight,
        tileBasketLine,
        basketControlsDisabled,
        t,
        className,
        ...rest
    }: ProductTileCustomVerticalCardProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const hasBasketQuantity = tileBasketLine?.itemId != null && getDisplayQuantity(tileBasketLine, 0) >= 1;

    return (
        <div
            ref={ref}
            data-testid="product-tile"
            className={cn(
                'relative h-full w-full min-w-0 max-w-full cursor-default gap-0 border-0 py-0',
                'flex flex-col gap-0 rounded-3xl bg-card text-card-foreground',
                className
            )}
            {...rest}>
            <div
                className={cn(
                    'relative z-20 mb-4 shadow-[0_4px_12px_0_rgba(0,0,0,0.05)] aspect-square w-full min-w-0 max-w-full rounded-lg',
                    SAND_PLACEHOLDER
                )}>
                {/** Figma: 16px image corners; wishlist + PIM tag row (inset p-3). */}
                <ProductTileImageCustom
                    variant="plp-vertical"
                    product={product}
                    pdpLink={false}
                    imgAspectRatio={effectiveImgAspectRatio}
                    handleProductClick={handleProductClick}
                />
                {/** Sits under tags/wishlist; `z-20` on the column above the full-card `Link` so it receives pointer hits. */}
                <Link
                    to={productUrl}
                    tabIndex={-1}
                    aria-hidden
                    className="absolute inset-0 z-[1] rounded-lg"
                    onClick={() => handleProductClick?.(product)}
                />
                <div className="pointer-events-none absolute inset-0 z-[5]">
                    <div className="flex w-full min-w-0 items-center justify-between gap-2 p-3">
                        {/** Sale % lives next to price only; not on the image row. */}
                        <div className="pointer-events-none flex min-w-0 flex-1 flex-wrap content-start items-center gap-1">
                            <ProductTileTagOverflowRow
                                highlight={productHighlight}
                                eatingStyles={eatingList}
                                // Reserve a consistent row height even when no tags.
                                className="min-h-5"
                            />
                        </div>
                        <div className="pointer-events-auto z-20 flex shrink-0 self-start">
                            <WishlistIconButton product={product} className="size-6 text-[var(--neutral-20)]" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="product-card-content-container flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4 pl-0 pr-2 pb-4">
                <div className="flex w-full min-w-0 flex-col gap-1">
                    <p className="body-copy-m m-0 w-full min-w-0 text-[var(--neutral-40)]">{brand || '\u00A0'}</p>
                    <h3 className="body-copy-l-bold m-0 h-12 w-full min-w-0 line-clamp-2 text-left text-[var(--neutral-10)]">
                        {productName}
                    </h3>
                </div>

                {priceAndWeight}

                <div className="pointer-events-none relative z-20">
                    <ProductTileCartOrQuickAdd
                        product={product}
                        productName={productName}
                        display="tag"
                        className={hasBasketQuantity ? 'w-full min-w-0' : ''}
                        quantityLayout="horizontal"
                        basketLine={tileBasketLine}
                        disabled={basketControlsDisabled === true}
                        productUrl={productUrl}
                    />
                </div>
            </div>

            <Link
                to={productUrl}
                className="peer absolute inset-0 z-10 rounded-3xl focus-visible:outline-none"
                aria-label={t('viewProductAriaLabel', { productName }) ?? `View ${productName}`}
                onClick={() => handleProductClick?.(product)}
            />
            <div aria-hidden className={productCardFocusRingClass} />
        </div>
    );
}

const ProductTileCustomVerticalCard = forwardRef(ProductTileCustomVerticalCardInner);

export { ProductTileCustomVerticalCard };
